import { makeObservable, runInAction, action, observable, computed } from "mobx";
import { WithAppStore } from "@/store/types";
import { AppStore } from "@/store";
import { CacheKey } from "@/modules/cache/constants";
import { BaseError } from "@/domains/errors";
import { objectModule } from "@/modules/object";
import { logger } from "@/modules/logger";
import { asyncResultModule } from "@/modules/async-result";
import { AsyncResult } from "@/modules/async-result/types";

type PerDeviceOnboardingInfoData = {
  hasDismissedMeetingOnboardingSystemPermissions: boolean;
  hasDismissedVoiceOnboardingSystemPermissions: boolean;
  hasCompletedOrSkippedNativeAppDownload: boolean;
};

const DEFAULT_PER_DEVICE_ONBOARDING_INFO_DATA: PerDeviceOnboardingInfoData = {
  hasDismissedMeetingOnboardingSystemPermissions: false,
  hasDismissedVoiceOnboardingSystemPermissions: false,
  hasCompletedOrSkippedNativeAppDownload: false,
};

export class PerDeviceOnboardingInfoObservable {
  store: AppStore;
  private deviceOnboardingInfo: AsyncResult<PerDeviceOnboardingInfoData> =
    asyncResultModule.setLoading();

  constructor({ store }: WithAppStore) {
    this.store = store;

    makeObservable<
      PerDeviceOnboardingInfoObservable,
      | "deviceOnboardingInfo"
      | "currentOnboardingSystemPermissionsData"
      | "shouldShowMeetingOnboardingSystemPermissions"
      | "shouldShowVoiceOnboardingSystemPermissions"
      | "hasCompletedOrSkippedNativeAppDownload"
      | "completeOrSkipNativeAppDownload"
      | "_resolveOnboardingInfoDataBasedOnDeviceState"
      | "_hydrateOnboardingInfo"
    >(this, {
      store: false,
      deviceOnboardingInfo: observable,
      shouldShowMeetingOnboardingSystemPermissions: computed,
      shouldShowVoiceOnboardingSystemPermissions: computed,
      currentOnboardingSystemPermissionsData: computed,
      _resolveOnboardingInfoDataBasedOnDeviceState: action,
      _hydrateOnboardingInfo: false,
      initialize: action,
      resetState: action,
      dismissMeetingOnboardingSystemPermissions: action,
      dismissVoiceOnboardingSystemPermissions: action,
      debugResetMeetingPermissions: action,
      debugResetVoicePermissions: action,
      debugResetNativeAppDownload: action,
      hasCompletedOrSkippedNativeAppDownload: computed,
      completeOrSkipNativeAppDownload: action,
    });
  }

  /**
   * Note that we use the `inverse` here instead of hasDismissedOnboardingSystemPermissions`.
   *
   * This is because:
   * - If we hit a snag when hydrating, we want to default to -not- showing the onboarding steps.
   */
  get shouldShowMeetingOnboardingSystemPermissions(): boolean {
    if (!this.deviceOnboardingInfo.data) return false;
    if (!this.deviceOnboardingInfo.data.hasDismissedMeetingOnboardingSystemPermissions) {
      return true;
    }

    const enabled = this.store.systemPermissions.meetingNotificationEnabled;
    if (!enabled) return false;

    // Do we have the required permissions?
    return !this.store.systemPermissions.hasRequiredMeetingPermissions;
  }

  get shouldShowVoiceOnboardingSystemPermissions(): boolean {
    if (!this.deviceOnboardingInfo.data) return false;
    return !this.deviceOnboardingInfo.data.hasDismissedVoiceOnboardingSystemPermissions;
  }

  get hasCompletedOrSkippedNativeAppDownload(): boolean {
    return this.deviceOnboardingInfo.data?.hasCompletedOrSkippedNativeAppDownload ?? false;
  }

  completeOrSkipNativeAppDownload() {
    runInAction(() => {
      const currentData = this.currentOnboardingSystemPermissionsData;

      const nextData: PerDeviceOnboardingInfoData = {
        ...currentData,
        hasCompletedOrSkippedNativeAppDownload: true,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  private get currentOnboardingSystemPermissionsData(): PerDeviceOnboardingInfoData {
    return this.deviceOnboardingInfo.data ?? DEFAULT_PER_DEVICE_ONBOARDING_INFO_DATA;
  }

  dismissMeetingOnboardingSystemPermissions() {
    runInAction(() => {
      const currentData = this.currentOnboardingSystemPermissionsData;

      const nextData: PerDeviceOnboardingInfoData = {
        ...currentData,
        hasDismissedMeetingOnboardingSystemPermissions: true,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  dismissVoiceOnboardingSystemPermissions() {
    runInAction(() => {
      const currentData = this.currentOnboardingSystemPermissionsData;

      const nextData: PerDeviceOnboardingInfoData = {
        ...currentData,
        hasDismissedVoiceOnboardingSystemPermissions: true,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  debugResetMeetingPermissions() {
    runInAction(() => {
      const currentData = this.currentOnboardingSystemPermissionsData;

      const nextData: PerDeviceOnboardingInfoData = {
        ...currentData,
        hasDismissedMeetingOnboardingSystemPermissions: false,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  debugResetVoicePermissions() {
    runInAction(() => {
      const currentData = this.currentOnboardingSystemPermissionsData;

      const nextData: PerDeviceOnboardingInfoData = {
        ...currentData,
        hasDismissedVoiceOnboardingSystemPermissions: false,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  debugResetNativeAppDownload() {
    runInAction(() => {
      const currentData = this.currentOnboardingSystemPermissionsData;

      const nextData: PerDeviceOnboardingInfoData = {
        ...currentData,
        hasCompletedOrSkippedNativeAppDownload: false,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  /**
   * This determines the "initial" state for users.
   */
  private async _resolveOnboardingInfoDataBasedOnDeviceState(): Promise<PerDeviceOnboardingInfoData> {
    return {
      hasDismissedMeetingOnboardingSystemPermissions: false,
      hasDismissedVoiceOnboardingSystemPermissions: false,
      hasCompletedOrSkippedNativeAppDownload: false,
    };
  }

  private async _hydrateOnboardingInfo() {
    try {
      let cachedData = await this.store.accountCache.get(CacheKey.PerDeviceOnboardingInfo);

      // Migration path: if accountCache is empty, check globalCache and migrate
      if (!cachedData) {
        const globalCacheData = await this.store.publicAppStore.globalCache.get(
          CacheKey.PerDeviceOnboardingInfo
        );

        if (globalCacheData) {
          this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, globalCacheData);
          cachedData = globalCacheData;

          logger.info({
            message:
              "[PerDeviceOnboardingInfoObservable] Migrated onboarding info from globalCache to accountCache",
          });
        }
      }

      if (!cachedData) {
        const resolvedData = await this._resolveOnboardingInfoDataBasedOnDeviceState();

        this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, resolvedData);
        this.deviceOnboardingInfo = asyncResultModule.setReady(resolvedData);

        return;
      }

      runInAction(() => {
        this.deviceOnboardingInfo = asyncResultModule.setReady({
          hasDismissedMeetingOnboardingSystemPermissions:
            cachedData.hasDismissedMeetingOnboardingSystemPermissions,
          hasDismissedVoiceOnboardingSystemPermissions:
            cachedData.hasDismissedVoiceOnboardingSystemPermissions,
          hasCompletedOrSkippedNativeAppDownload: cachedData.hasCompletedOrSkippedNativeAppDownload,
        });
      });
    } catch (unknownErr) {
      const err = unknownErr as BaseError;

      logger.warn({
        message: "[PerDeviceOnboardingInfoObservable] Failed to hydrate onboarding info",
        info: { error: objectModule.safeErrorAsJson(err) },
      });

      runInAction(() => {
        this.deviceOnboardingInfo = asyncResultModule.setError(err);
      });
    }
  }

  async initialize() {
    await this._hydrateOnboardingInfo();
  }

  resetState() {
    runInAction(() => {
      this.deviceOnboardingInfo = asyncResultModule.setInitial();
    });
  }
}
